/**
 * Keeping the step grid and high-resolution note events in agreement.
 *
 * A pattern clip can carry two representations of the same music:
 *
 *   pattern/noteData  the 16th-note step grid the sequencer draws
 *   noteEvents        exact ticks, so recorded timing and rolls are not
 *                     pre-snapped to that grid
 *
 * The engine *prefers* noteEvents whenever the array is non-empty and ignores
 * the grid entirely. So editing a step without touching noteEvents produces a
 * clip whose grid says one thing and whose playback does another — the edit
 * looks applied and is silently inaudible.
 *
 * These helpers apply a grid edit to the events too, using the same
 * floor(startTick / ticksPerStep) mapping the engine uses to derive the grid,
 * so the two representations cannot drift apart.
 */

export const DEFAULT_PPQ = 96;

/** The engine plays MIDI 72 at unity playback rate. */
export const DEFAULT_PITCH = 72;

export const ticksPerStep = (ppq = DEFAULT_PPQ) => (Number(ppq) || DEFAULT_PPQ) / 4;

/** Which step does an event land on? Mirrors the engine's own derivation. */
export const stepOfTick = (startTick, ppq = DEFAULT_PPQ) => (
  Math.max(0, Math.floor((Number(startTick) || 0) / ticksPerStep(ppq)))
);

/**
 * Apply a step toggle to a note-event list.
 *
 * Turning a step off removes every event that lands on it — a rolled step can
 * hold several — so silencing a step in the grid really silences it.
 * Turning a step on adds a single event at the step boundary, unless one is
 * already there (in which case the grid was merely out of date).
 *
 * Returns the original array when there is nothing to change, so callers can
 * skip pointless clip updates.
 */
export function toggleStepInNoteEvents(noteEvents, options = {}) {
  const {
    drumIndex,
    step,
    active,
    ppq = DEFAULT_PPQ,
    pitch = DEFAULT_PITCH,
    velocity = 1,
    id
  } = options;

  if (!Array.isArray(noteEvents)) return noteEvents;
  if (!Number.isFinite(drumIndex) || !Number.isFinite(step)) return noteEvents;

  const lands = (event) => (
    Number(event?.drumIndex) === Number(drumIndex)
    && stepOfTick(event?.startTick, ppq) === Number(step)
  );

  if (!active) {
    const next = noteEvents.filter((event) => !lands(event));
    return next.length === noteEvents.length ? noteEvents : next;
  }

  if (noteEvents.some(lands)) return noteEvents;

  const stepTicks = ticksPerStep(ppq);
  return [
    ...noteEvents,
    {
      id: id || `step-${drumIndex}-${step}-${noteEvents.length}`,
      drumIndex,
      startTick: Math.round(step * stepTicks),
      durationTicks: Math.round(stepTicks),
      pitch,
      velocity
    }
  ];
}

/**
 * Rewrite the pitch/velocity of the events on a step, so grid-level edits are
 * not swallowed by the events the engine actually plays.
 */
export function updateStepInNoteEvents(noteEvents, options = {}) {
  const { drumIndex, step, ppq = DEFAULT_PPQ, pitch, velocity } = options;
  if (!Array.isArray(noteEvents)) return noteEvents;
  if (!Number.isFinite(drumIndex) || !Number.isFinite(step)) return noteEvents;

  let changed = false;
  const next = noteEvents.map((event) => {
    if (Number(event?.drumIndex) !== Number(drumIndex)) return event;
    if (stepOfTick(event?.startTick, ppq) !== Number(step)) return event;
    changed = true;
    return {
      ...event,
      ...(Number.isFinite(pitch) ? { pitch } : {}),
      ...(Number.isFinite(velocity) ? { velocity } : {})
    };
  });
  return changed ? next : noteEvents;
}

/** Drop every event for a lane — the counterpart to clearing a grid row. */
export function clearLaneInNoteEvents(noteEvents, drumIndex) {
  if (!Array.isArray(noteEvents)) return noteEvents;
  const next = noteEvents.filter((event) => Number(event?.drumIndex) !== Number(drumIndex));
  return next.length === noteEvents.length ? noteEvents : next;
}

/** Toggle one piano pitch on a 16th-note cell without disturbing other lanes. */
export function togglePitchAtStep(noteEvents, options = {}) {
  const {
    drumIndex,
    step,
    pitch,
    ppq = DEFAULT_PPQ,
    velocity = 1,
    id
  } = options;
  if (!Array.isArray(noteEvents)) return noteEvents;
  if (![drumIndex, step, pitch].every(Number.isFinite)) return noteEvents;

  const lands = (event) => (
    Number(event?.drumIndex) === Number(drumIndex)
    && stepOfTick(event?.startTick, ppq) === Number(step)
  );
  const pitchesOf = (event) => [...new Set(
    (Array.isArray(event?.pitches) ? event.pitches : [event?.pitch])
      .map(Number)
      .filter(Number.isFinite)
  )];
  const pitchExists = noteEvents.some((event) => lands(event) && pitchesOf(event).includes(Number(pitch)));

  if (pitchExists) {
    return noteEvents.flatMap((event) => {
      if (!lands(event)) return [event];
      const nextPitches = pitchesOf(event).filter((value) => value !== Number(pitch));
      if (nextPitches.length === pitchesOf(event).length) return [event];
      if (!nextPitches.length) return [];
      return [{ ...event, pitch: nextPitches[0], pitches: nextPitches }];
    });
  }

  const firstIndex = noteEvents.findIndex(lands);
  if (firstIndex >= 0) {
    return noteEvents.map((event, index) => {
      if (index !== firstIndex) return event;
      const nextPitches = [...new Set([...pitchesOf(event), Number(pitch)])].sort((a, b) => a - b);
      return { ...event, pitch: nextPitches[0], pitches: nextPitches };
    });
  }

  const stepTicks = ticksPerStep(ppq);
  return [...noteEvents, {
    id: id || `piano-${drumIndex}-${step}-${pitch}-${noteEvents.length}`,
    drumIndex,
    startTick: Math.round(step * stepTicks),
    durationTicks: Math.round(stepTicks),
    pitch,
    pitches: [pitch],
    velocity
  }];
}

/** Replace one lane from imported step notes while preserving every other lane. */
export function replaceLaneWithStepNotes(noteEvents, drumIndex, notes = [], ppq = DEFAULT_PPQ) {
  if (!Array.isArray(noteEvents) || !Number.isFinite(drumIndex)) return noteEvents;
  const stepTicks = ticksPerStep(ppq);
  const grouped = new Map();
  notes.forEach((note) => {
    const step = Number(note?.step);
    const pitch = Number(note?.pitch);
    if (!Number.isFinite(step) || !Number.isFinite(pitch)) return;
    const bucket = grouped.get(step) || { pitches: [], velocity: Number(note?.velocity) };
    bucket.pitches.push(pitch);
    if (Number.isFinite(note?.velocity)) bucket.velocity = Number(note.velocity);
    grouped.set(step, bucket);
  });
  const laneEvents = [...grouped.entries()].map(([step, note], index) => {
    const pitches = [...new Set(note.pitches)].sort((a, b) => a - b);
    return {
      id: `midi-${drumIndex}-${step}-${index}`,
      drumIndex,
      startTick: Math.round(step * stepTicks),
      durationTicks: Math.round(stepTicks),
      pitch: pitches[0],
      pitches,
      velocity: Number.isFinite(note.velocity) ? note.velocity : 1
    };
  });
  return [...clearLaneInNoteEvents(noteEvents, drumIndex), ...laneEvents]
    .sort((a, b) => (Number(a?.startTick) || 0) - (Number(b?.startTick) || 0));
}

/**
 * Every pitch a note event sounds.
 *
 * A chord is stored as ONE event carrying several pitches, so reading only
 * `pitches[0]` plays the root and silently drops the rest of the chord. The
 * drum path has always expanded these; the instrument paths did not, which is
 * why a C major chord sounded as a bare C.
 */
export function eventPitches(event, fallback = DEFAULT_PITCH) {
  const raw = Array.isArray(event?.pitches) && event.pitches.length > 0
    ? event.pitches
    : [event?.midiNote ?? event?.pitch ?? fallback];
  const seen = new Set();
  const pitches = [];
  raw.forEach((value) => {
    const midi = Math.round(Number(value));
    if (!Number.isFinite(midi) || midi < 0 || midi > 127) return;
    if (seen.has(midi)) return;
    seen.add(midi);
    pitches.push(midi);
  });
  // An event that names no usable pitch still has to make a sound, or a bad
  // value would silently swallow the note.
  return pitches.length > 0 ? pitches : [Math.round(Number(fallback)) || DEFAULT_PITCH];
}
